import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Guid } from '@app/core/constants/constants';
import { environment } from '@env/environment';
import { Observable, map, retry, catchError, throwError } from 'rxjs';
import { appInjector } from '../classes/app-injector';
import { OperationResult } from './models/operation-result';
import { DataService } from '@app/core/services/data.service';
import { PhotoRelation } from '@app/core/constants/global-enum';
import { GlobalVariablesService } from '@app/core/services/global-variables.service';
@Injectable({
  providedIn: 'root'
})
export class ImageService {
  public operationCompleted: EventEmitter<OperationResult<Guid>> = new EventEmitter();

  public httpClient: HttpClient;
  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };
  constructor(private dataService: DataService, private globals: GlobalVariablesService) {
    this.httpClient = appInjector.get(HttpClient);
  }

  public getImageUrl(imageId: Guid, aiCrop: number = 0.8, cacheId: Guid = Guid.newGuid(), width: number = 256): string {
    if (aiCrop == 0)
      return `${this.globals.photoServerAddress}/content/images/${imageId}/original.jpg?width=${width}&ci=${cacheId}`;
    return `${this.globals.photoServerAddress}/content/images/${imageId}/original.jpg?width=${width}&content-aware=${aiCrop}&ci=${cacheId}`;
  }

  public keepImage(imageId: Guid): Observable<void> {
    return this.httpClient.put<void>(`${this.globals.photoServerApiAddress}/image/${imageId}/keep`, this.httpOptions)
      .pipe(
        map((data) => {
          return data;
        }),
        retry(3),
        catchError(this.handleError)
      );
  }

  handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = '';

    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = ` ${error.status}, ` + `message: ${error.message}`;
    }

    return throwError(() => error);
  }

  temporaryTrackItemImage(imageUrl: string): string {
    return this.dataService.photoLink(imageUrl, PhotoRelation.TrackItem);
  }

  public temporaryImage(imageUrl: string, relation: PhotoRelation): string {
    return this.dataService.photoLink(imageUrl, relation);
  }

  /**
   * Adaptive image getter that handles both JSON format and direct URLs
   * @param imageData - Can be JSON string like '{"id":"guid"}' or direct URL
   * @param aiCrop - AI crop factor (default: 0.8)
   * @param width - Image width (default: 256)
   * @returns Image URL ready for display
   */
  public getAdaptiveImage(imageData: string | null | undefined, aiCrop: number = 0.8, width: number = 256): string | null {
    return this.getAdaptiveImageForRelation(imageData, PhotoRelation.TrackItem, aiCrop, width);
  }

  /**
   * Relation-aware form of getAdaptiveImage. The relation is only used for
   * legacy photo identifiers; JSON image ids and absolute/photo-server URLs
   * keep their existing resolution path.
   */
  public getAdaptiveImageForRelation(
    imageData: string | null | undefined,
    relation: PhotoRelation,
    aiCrop: number = 0.8,
    width: number = 256
  ): string | null {
    if (!imageData) {
      return null;
    }

    const normalized = imageData.trim();
    if (!normalized) {
      return null;
    }

    // Absolute URLs should be used as-is.
    if (this.isAbsoluteUrl(normalized)) {
      return normalized;
    }

    // Application-bundled presentation assets are already browser-ready paths.
    if (normalized.startsWith('/assets/') || normalized.startsWith('assets/')) {
      return normalized;
    }

    // Relative media paths from photo server should be normalized.
    if (normalized.startsWith('/content/images/') || normalized.startsWith('/photos/')) {
      return `${this.globals.photoServerAddress}${normalized}`;
    }
    if (normalized.startsWith('content/images/') || normalized.startsWith('photos/')) {
      return `${this.globals.photoServerAddress}/${normalized}`;
    }

    try {
      // Try to parse as JSON first (new format)
      const parsedData = JSON.parse(normalized);
      if (parsedData && parsedData.id) {
        // It's JSON format, use the ID to get proper image URL
        return this.getImageUrl(parsedData.id, aiCrop, parsedData.id, width);
      }

      // If JSON payload already contains a full URL/path, prefer it directly.
      if (parsedData && typeof parsedData.url === 'string') {
        const urlValue = parsedData.url.trim();
        if (this.isAbsoluteUrl(urlValue)) {
          return urlValue;
        }
        if (urlValue.startsWith('/content/images/') || urlValue.startsWith('/photos/')) {
          return `${this.globals.photoServerAddress}${urlValue}`;
        }
        if (urlValue.startsWith('content/images/') || urlValue.startsWith('photos/')) {
          return `${this.globals.photoServerAddress}/${urlValue}`;
        }
      }
    } catch (e) {
      // Not JSON, treat as legacy format
      return this.temporaryImage(normalized, relation);
    }

    // Fallback for any other case
    return this.temporaryImage(normalized, relation);
  }

  public getTrackItemImage(imageData: string | null | undefined, aiCrop: number = 0.8, width: number = 256): string | null {
    return this.getAdaptiveImageForRelation(imageData, PhotoRelation.TrackItem, aiCrop, width);
  }

  public getTagImage(imageData: string | null | undefined, aiCrop: number = 0.8, width: number = 256): string | null {
    return this.getAdaptiveImageForRelation(imageData, PhotoRelation.Tag, aiCrop, width);
  }

  private isAbsoluteUrl(value: string): boolean {
    return /^https?:\/\//i.test(value) || /^data:/i.test(value) || /^blob:/i.test(value);
  }

  public imageError(event: any): void {
    event.target.src = 'assets/images/placeholders/placeholder-large.png';
}

}
